import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/CallToAction.css';

const CallToAction = () => {
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes in seconds
  const controls = useAnimation();
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const videoRef = useRef(null);

  useEffect(() => {
    // Check if there's a saved timer in localStorage
    const savedTime = localStorage.getItem('cta-timer');
    const currentTime = Date.now();

    if (savedTime) {
      const savedData = JSON.parse(savedTime);
      const elapsedTime = Math.floor((currentTime - savedData.timestamp) / 1000);
      const updatedTimeLeft = savedData.timeLeft - elapsedTime;
      setTimeLeft(Math.max(updatedTimeLeft, 0)); // Don't let timeLeft go below 0
    }

    // Start the timer
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          localStorage.removeItem('cta-timer');
          return 0;
        }
        const newTime = prevTime - 1;
        localStorage.setItem('cta-timer', JSON.stringify({ timeLeft: newTime, timestamp: Date.now() }));
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (inView) {
      controls.start({ opacity: 1, y: 0 });
    }
  }, [inView, controls]);

  // Ensure video plays on iOS devices
  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      
      // Set required attributes for iOS
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.muted = true;
      video.autoplay = true;
      
      // Force play on iOS
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('CTA video autoplay started');
          })
          .catch((error) => {
            console.log('CTA video autoplay prevented:', error);
          });
      }
    }
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.section
      ref={ref}
      className="cta-section"
      initial={{ opacity: 0, y: 50 }}
      animate={controls}
      transition={{ duration: 1 }}
    >
      <div className="cta-content">
        <motion.div 
          className="cta-text"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h2>Quantum Leap into Savings!</h2>
          <p>Experience the future of design with QuantumFrame Studio. Claim your exclusive discount now and revolutionize your creative process!</p>
          <div className="cta-timer">
            <span>Time Left: </span>
            <motion.span 
              className="timer"
              key={timeLeft}
              initial={{ scale: 1.5, color: "#00FFFF" }}
              animate={{ scale: 1, color: "#E60023" }}
              transition={{ duration: 0.5 }}
            >
              {formatTime(timeLeft)}
            </motion.span>
          </div>
          <motion.button 
            className="cta-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Claim Your Quantum Discount
          </motion.button>
        </motion.div>
        <motion.div 
          className="cta-video"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <video 
            ref={videoRef}
            className="cta-video-element" 
            autoPlay 
            loop 
            muted 
            playsInline
            webkit-playsinline="true"
            preload="auto"
            style={{ pointerEvents: 'none' }}
          >
            <source src="assets/video/discount-cta.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default CallToAction;
